import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastState {
  toasts: Toast[];
  showToast: (message: string, kind?: ToastKind) => number;
  dismissToast: (id: number) => void;
  clearToasts: () => void;
}

let nextToastId = 1;

/**
 * Global toast store. Kept intentionally dumb — a ring-buffer of
 * currently-visible toasts with imperative show/dismiss actions. Auto-dismiss
 * timing is handled by the <ToastViewport /> component so tests can control
 * it via fake timers without racing against store internals.
 */
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  showToast: (message, kind = 'success') => {
    const id = nextToastId++;
    set((prev) => ({
      toasts: [...prev.toasts, { id, message, kind }],
    }));
    return id;
  },
  dismissToast: (id) => {
    set((prev) => ({ toasts: prev.toasts.filter((t) => t.id !== id) }));
  },
  clearToasts: () => {
    set({ toasts: [] });
  },
}));

/**
 * Hook wrapper. Using a stable bound action here avoids subscribing
 * components to the `toasts` array when they only want to emit toasts.
 */
export function useToast() {
  return {
    showToast: useToastStore((s) => s.showToast),
  };
}
